import { createSlice } from "@reduxjs/toolkit";
import { getTodayEndISO, getTodayStartISO } from "Utils";
import { deepCamelCaseKeys } from "Utils/collectionUtils";

import { getSavedPeriod, getSavedVehiclesIds } from "./utils";
import { RowData } from "Components/NewList/types";

export const getDefaultPeriod = () => ({
  startDate: getTodayStartISO(),
  endDate: getTodayEndISO(),
});

type MissionPlanner = {
  isOrderMissions: boolean;
  currentFleetId: string | null;
  period: MissionPlannerPeriodFilter | null;
  searchValue: string;
  activeTab: MissionPlannerTabName;
  headerData: {
    name: string;
    version: string;
    assignedTo: string;
    status: string;
    stops: string;
    vdaActions?: string;
    loops: string;
  };
  limit: number;
  allMissionsCount: number;
  completedMissionsCount: number;
  deployedMissionsCount: number;
  missionsQueue: null | Array<Mission>;
  selectedVehicleIds: Array<string>;
  isUpdating: boolean;
  isMissionsUpdatesPaused: null | boolean;
  missionsUpdatesStorage: Array<Mission | never>;
};

const initialState: MissionPlanner = {
  isOrderMissions: false,
  currentFleetId: null,
  period: null,
  searchValue: "",
  activeTab: "All",
  headerData: {
    name: "Mission Name",
    version: "Version",
    assignedTo: "Vehicle Assigned",
    status: "Status",
    stops: "Stops",
    vdaActions: "Actions",
    loops: "Cycles",
  },
  limit: 20,
  allMissionsCount: 0,
  completedMissionsCount: 0,
  deployedMissionsCount: 0,
  missionsQueue: null,
  selectedVehicleIds: [],
  isUpdating: false,
  isMissionsUpdatesPaused: false,
  missionsUpdatesStorage: [],
};

const cachedInitialState = {
  ...initialState,
  period: getSavedPeriod(),
  selectedVehicleIds: getSavedVehiclesIds(),
};

export const Slice = createSlice({
  name: "missionPlanner",
  initialState: cachedInitialState,
  reducers: {
    resetMissionPlannerData: () => initialState,
    setIsMissionPlannerUpdating: (state, action) => {
      state.isUpdating = action.payload;
      return state;
    },
    setCurrentFleetId: (state, action) => {
      state.currentFleetId = action.payload;
      return state;
    },
    setCurrentFleetPeriod: (state, action) => {
      state.period = action.payload;
      return state;
    },
    setMissionRowsLimit: (state, action) => {
      state.limit = action.payload;
      return state;
    },
    setCurrentSearchValue: (state, action) => {
      state.searchValue = action.payload;
      return state;
    },
    setHeaderData: (state, action) => {
      state.headerData = action.payload;
      return state;
    },
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
      return state;
    },
    setMissionCounts: (state, action) => {
      state.allMissionsCount = action.payload.count;
      state.deployedMissionsCount = action.payload.deployedCount;
      state.completedMissionsCount = action.payload.completedCount;
      return state;
    },
    resetMissionsQueue: (state) => {
      state.missionsQueue = null;
      state.allMissionsCount = 0;
      state.completedMissionsCount = 0;
      state.deployedMissionsCount = 0;
      state.limit = 20;
      return state;
    },
    setMissionsQueue: (state, action) => {
      state.missionsQueue = action.payload;
      return state;
    },
    reorderMissionsQueue: (state, action) => {
      const reorderedMissions = action.payload
        ?.map?.((id: RowData["id"]) => {
          const foundedReorderedItem = state.missionsQueue?.find(
            (item) => item.id === id
          );
          return foundedReorderedItem;
        })
        .filter(Boolean);

      state.missionsQueue = reorderedMissions
        ? reorderedMissions
        : state.missionsQueue;
      return state;
    },
    updateMissionsQueue: (state, action) => {
      const receivedMission = deepCamelCaseKeys(action.payload);
      if (!receivedMission) return state;

      if (state.isMissionsUpdatesPaused) {
        state.missionsUpdatesStorage.push(action.payload?.data);
        return state;
      }

      const isNewMission = !state.missionsQueue?.find(
        (mission) => mission?.id === receivedMission?.id
      );

      if (isNewMission) {
        const currentSelectedVehicleIds = state.selectedVehicleIds;

        const noSelectedVehicles = currentSelectedVehicleIds.length === 0;
        const newMissionIsPartOfSelectedVehicles =
          currentSelectedVehicleIds.includes(receivedMission?.vehicle);

        if (noSelectedVehicles || newMissionIsPartOfSelectedVehicles) {
          state.missionsQueue = [
            receivedMission,
            ...(state.missionsQueue?.length ? state.missionsQueue : []),
          ];
        }
      } else {
        state.missionsQueue =
          state.missionsQueue?.map((mission) =>
            mission.id === receivedMission?.id
              ? {
                  ...mission,
                  vehicle: receivedMission?.vehicle,
                  status: receivedMission?.status,
                  passedLoops: receivedMission?.passedLoops,
                  passedStops: receivedMission?.passedStops,
                  stops: receivedMission?.stops,
                }
              : mission
          ) ?? [];
      }
      return state;
    },
    setSelectedVehicleIds: (state, action) => {
      state.selectedVehicleIds = action.payload.selected
        ? [...state.selectedVehicleIds, action.payload.vehicleId]
        : state.selectedVehicleIds.filter(
            (selectedVehicleId) =>
              selectedVehicleId !== action.payload.vehicleId
          );
      return state;
    },
    resetPagination: (state) => {
      state.limit = 20;
      return state;
    },
    pauseMissionsUpdates: (state) => {
      state.isMissionsUpdatesPaused = true;
      return state;
    },
    continueMissionsUpdates: (state) => {
      state.isMissionsUpdatesPaused = false;
      return state;
    },
    resetMissionsUpdatesStorage: (state) => {
      state.missionsUpdatesStorage = [];
      return state;
    },
  },
});

export default Slice;
